import { ArrowUpRight } from "lucide-react";

/* Shared primitives for the Figma landing redesign (Frame 130).
   Kept in one file so the section components stay focused on layout. */

/** Most eyebrows are 47px tall; the FAQs pill is 60px. The override is inline so
    it reliably beats the base h-[47px] utility. */
export function Eyebrow({ children, className = "", height }) {
  return (
    <div
      className={`inline-flex h-[47px] items-center justify-center rounded-[45px] border border-black px-7 font-poppins text-[13px] uppercase text-black sm:text-[16px] ${className}`}
      style={height ? { height } : undefined}
    >
      {children}
    </div>
  );
}

/** Rotated marker rectangle behind a heading word.

    Across every Figma instance the rect height equals the heading's line box, so
    host headings must set `leading-[1.65]` or the marker reads as a thin strip.
    Horizontal overhang is ~0.25em, and `mx` must exceed it or the rect butts into
    the neighbouring word — visible gap = mx − overhang. All in `em` so the
    proportions hold at every responsive step. */
export function Marker({ children, color = "var(--lime)", rotate = "-2deg", className = "" }) {
  return (
    <span className={`relative mx-[0.5em] inline-block ${className}`}>
      <span
        className="absolute -inset-x-[0.25em] inset-y-0 rounded-[0.42em]"
        style={{ background: color, transform: `rotate(${rotate})` }}
        aria-hidden
      />
      <span className="relative">{children}</span>
    </span>
  );
}

export function ArrowBubble({ bg = "#000", fg = "text-white", size = 36, icon = 18, className = "" }) {
  return (
    <span
      className={`flex shrink-0 items-center justify-center rounded-full ${className}`}
      style={{ background: bg, width: size, height: size }}
    >
      <ArrowUpRight size={icon} className={fg} />
    </span>
  );
}

export function PillCta({ children, onClick, href, bubble = "#000", bubbleFg = "text-white", bg = "#fff", className = "" }) {
  const inner = (
    <>
      {children}
      <ArrowBubble bg={bubble} fg={bubbleFg} className="transition-transform group-hover:scale-105" />
    </>
  );
  const cls = `group inline-flex h-14 items-center justify-center gap-3 rounded-[45px] pl-[22px] pr-[11px] font-poppins text-[18px] text-black transition-all hover:brightness-95 active:scale-95 ${className}`;
  if (href) {
    return (
      <a href={href} target="_blank" rel="noopener noreferrer" className={cls} style={{ background: bg }}>
        {inner}
      </a>
    );
  }
  return (
    <button onClick={onClick} className={cls} style={{ background: bg }}>
      {inner}
    </button>
  );
}

export function SectionHead({ children, className = "" }) {
  return (
    <h2 className={`font-poppins font-medium leading-[1.2] text-black ${className}`}>{children}</h2>
  );
}
